import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, Download, Eye, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const OCR: React.FC = () => {
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState<any[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'pending',
      preview: URL.createObjectURL(file)
    }));
    
    setUploadedFiles(prev => [...prev, ...newFiles]);
    toast.success(`${acceptedFiles.length} archivo(s) cargado(s)`);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp'],
      'application/pdf': ['.pdf']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: true
  });

  const processFiles = async () => {
    if (uploadedFiles.length === 0) {
      toast.error('No hay archivos para procesar');
      return;
    }

    setProcessing(true);
    
    // Simular procesamiento OCR
    setTimeout(() => {
      const processedResults = uploadedFiles.map(file => ({
        id: file.id,
        name: file.name,
        type: file.type,
        extractedText: generateMockOCRText(file.name),
        confidence: Math.random() * 0.3 + 0.7, // 70-100%
        fields: extractFields(file.name),
        status: 'completed'
      }));
      
      setResults(processedResults);
      setProcessing(false);
      toast.success('Procesamiento OCR completado');
    }, 3000);
  };

  const generateMockOCRText = (filename: string) => {
    const mockTexts = {
      'cedula': 'REPÚBLICA DE COLOMBIA\nCÉDULA DE CIUDADANÍA\nNOMBRE: JUAN CARLOS PÉREZ GONZÁLEZ\nDOCUMENTO: 12345678\nFECHA DE NACIMIENTO: 15/03/1985\nLUGAR: BOGOTÁ D.C.',
      'licencia': 'REPÚBLICA DE COLOMBIA\nLICENCIA DE CONDUCCIÓN\nNOMBRE: MARÍA FERNÁNDEZ LÓPEZ\nDOCUMENTO: 87654321\nCATEGORÍA: B1\nVIGENCIA: 15/03/2029\nRESTRICCIONES: NINGUNA',
      'tarjeta': 'TARJETA DE PROPIEDAD\nVEHÍCULO: CHEVROLET SPARK\nPLACA: ABC123\nMODELO: 2020\nCOLOR: BLANCO\nPROPIETARIO: JUAN PÉREZ\nVIN: 1G1ZT53806F109149',
      'soat': 'SEGURO OBLIGATORIO DE ACCIDENTES DE TRÁNSITO\nPOLIZA: 1234567890\nVEHÍCULO: CHEVROLET SPARK\nPLACA: ABC123\nVIGENCIA: 31/12/2024\nASEGURADORA: SEGUROS BOLÍVAR'
    };

    const lowerFilename = filename.toLowerCase();
    for (const [key, value] of Object.entries(mockTexts)) {
      if (lowerFilename.includes(key)) {
        return value;
      }
    }
    
    return 'TEXTO EXTRAÍDO DEL DOCUMENTO\n\nEste es un ejemplo de texto extraído mediante OCR.\nEl sistema ha procesado el documento y extraído la información relevante.\n\nConfianza: 85%';
  };

  const extractFields = (filename: string) => {
    const lowerFilename = filename.toLowerCase();
    
    if (lowerFilename.includes('cedula')) {
      return {
        'Tipo Documento': 'Cédula de Ciudadanía',
        'Número': '12345678',
        'Nombre': 'JUAN CARLOS PÉREZ GONZÁLEZ',
        'Fecha Nacimiento': '15/03/1985',
        'Lugar': 'BOGOTÁ D.C.'
      };
    }
    
    if (lowerFilename.includes('licencia')) {
      return {
        'Tipo Documento': 'Licencia de Conducción',
        'Número': '87654321',
        'Nombre': 'MARÍA FERNÁNDEZ LÓPEZ',
        'Categoría': 'B1',
        'Vigencia': '15/03/2029'
      };
    }
    
    if (lowerFilename.includes('tarjeta')) {
      return {
        'Tipo Documento': 'Tarjeta de Propiedad',
        'Marca': 'CHEVROLET',
        'Modelo': 'SPARK',
        'Placa': 'ABC123',
        'Año': '2020',
        'Color': 'BLANCO'
      };
    }
    
    return {
      'Tipo': 'Documento General',
      'Confianza': '85%',
      'Fecha Procesamiento': new Date().toLocaleDateString()
    };
  };

  const removeFile = (id: number) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== id));
    setResults(prev => prev.filter(result => result.id !== id));
    toast.success('Archivo eliminado');
  };

  const downloadResult = (result: any) => {
    const blob = new Blob([result.extractedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.name}_ocr.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Resultado descargado');
  };

  const viewResult = (result: any) => {
    toast.success(`Viendo resultado de ${result.name}`);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Procesamiento OCR</h1>
        <p className="text-gray-600">Extrae texto e información de documentos usando reconocimiento óptico de caracteres</p>
      </div>

      {/* Upload Area */}
      <div className="card p-6">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input {...getInputProps()} />
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          {isDragActive ? (
            <p className="text-lg text-primary-600">Suelta los archivos aquí...</p>
          ) : (
            <div>
              <p className="text-lg text-gray-600 mb-2">
                Arrastra archivos aquí o haz clic para seleccionar
              </p>
              <p className="text-sm text-gray-500">
                Soporta imágenes (JPG, PNG, GIF, BMP) y PDFs
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Tamaño máximo: 10MB por archivo
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Archivos Cargados ({uploadedFiles.length})
            </h3>
            <button
              onClick={processFiles}
              disabled={processing}
              className="btn btn-primary disabled:opacity-50"
            >
              {processing ? 'Procesando...' : 'Procesar OCR'}
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {uploadedFiles.map((file) => (
              <div key={file.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <button
                    onClick={() => removeFile(file.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <h4 className="font-medium text-gray-900 text-sm truncate">{file.name}</h4>
                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                <p className="text-xs text-gray-500">{file.type}</p>
                <div className="mt-2">
                  {file.type.startsWith('image/') && (
                    <img
                      src={file.preview}
                      alt={file.name}
                      className="w-full h-20 object-cover rounded"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Processing Status */}
      {processing && (
        <div className="card p-6">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <p className="text-gray-600">Procesando documentos con OCR...</p>
          </div>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Resultados del Procesamiento ({results.length})
          </h3>
          
          {results.map((result) => (
            <div key={result.id} className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <FileText className="w-6 h-6 text-primary-600" />
                  <div>
                    <h4 className="font-medium text-gray-900">{result.name}</h4>
                    <p className="text-sm text-gray-500">
                      Confianza: {(result.confidence * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => viewResult(result)}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                    title="Ver resultado"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => downloadResult(result)}
                    className="p-2 text-green-600 hover:bg-green-100 rounded"
                    title="Descargar resultado"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Extracted Fields */}
              <div className="mb-4">
                <h5 className="font-medium text-gray-900 mb-2">Campos Extraídos:</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(result.fields).map(([key, value]) => (
                    <div key={key} className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-sm font-medium text-gray-600">{key}:</span>
                      <span className="text-sm text-gray-900">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Extracted Text */}
              <div>
                <h5 className="font-medium text-gray-900 mb-2">Texto Extraído:</h5>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                    {result.extractedText}
                  </pre>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Instructions */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Instrucciones de Uso</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Tipos de Documentos Soportados:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Cédulas de ciudadanía</li>
              <li>• Licencias de conducción</li>
              <li>• Tarjetas de propiedad</li>
              <li>• SOAT</li>
              <li>• Documentos generales</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Formatos de Archivo:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Imágenes: JPG, PNG, GIF, BMP, WebP</li>
              <li>• Documentos: PDF</li>
              <li>• Tamaño máximo: 10MB por archivo</li>
              <li>• Múltiples archivos simultáneos</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OCR;
